import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, FileText, Truck, CheckCircle, Leaf, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { ordersAPI } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { getPackageDisplay, getPricePerUnit } from '../lib/packageUtils';
import { toast } from 'sonner';

export const Checkout = () => {
    const navigate = useNavigate();
    const { orderItems: items, orderTotal: total, clearOrderItems: clearCart, sourceOfferId, calcItemTotal } = useCart();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [formData, setFormData] = useState({
        delivery_address: user?.address || '',
        notes: '',
    });

    if (items.length === 0) {
        return (
            <div className="min-h-screen bg-sand bg-botanical flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-jungle/10 flex items-center justify-center mb-4">
                        <Leaf className="w-8 h-8 text-jungle/50" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-jungle mb-4">Panier vide</h2>
                    <p className="text-muted-foreground mb-6">Ajoutez des produits avant de commander</p>
                    <Button asChild className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl">
                        <Link to="/catalogue">Voir le catalogue</Link>
                    </Button>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!confirmed) {
            toast.error('Veuillez confirmer votre commande en cochant la case');
            return;
        }
        
        if (!formData.delivery_address.trim()) {
            toast.error('Veuillez renseigner l\'adresse de livraison');
            return;
        }
        
        setLoading(true);
        try {
            const orderData = {
                items: items,
                delivery_address: formData.delivery_address,
                notes: formData.notes || null,
                offer_id: sourceOfferId || null,
            };
            const res = await ordersAPI.create(orderData);
            clearCart();
            toast.success(
                <div>
                    <p className="font-semibold">Commande {res.data.order_number} validée !</p>
                    <p className="text-sm">Un email de confirmation vous a été envoyé.</p>
                </div>
            );
            navigate(`/dashboard/commande/${res.data.id}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création de la commande');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="checkout-page">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 hover:bg-jungle/10 rounded-xl" data-testid="back-button">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Retour au panier
                </Button>

                <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 rounded-xl bg-leaf/10 flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-leaf" />
                    </div>
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">
                            Finaliser la commande
                        </h1>
                        <p className="text-muted-foreground">Vérifiez vos informations et validez</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Form */}
                    <div className="lg:col-span-2">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Client Info */}
                            <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 font-heading text-lg text-jungle">
                                        <Leaf className="h-5 w-5 text-leaf" />
                                        Informations client
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="bg-jungle/5 rounded-xl p-4 space-y-2">
                                        <p className="font-semibold text-jungle">{user?.company_name}</p>
                                        <p className="text-sm text-muted-foreground">Contact : {user?.contact_name}</p>
                                        <p className="text-sm text-muted-foreground">Email : {user?.email}</p>
                                        <p className="text-sm text-muted-foreground">Tél : {user?.phone}</p>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Delivery Address */}
                            <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 font-heading text-lg text-jungle">
                                        <MapPin className="h-5 w-5 text-leaf" />
                                        Adresse de livraison
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        <Label htmlFor="delivery_address" className="text-jungle">Adresse complète *</Label>
                                        <Textarea
                                            id="delivery_address"
                                            placeholder="123 Rue de la Convention, 75015 Paris"
                                            value={formData.delivery_address}
                                            onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                                            required
                                            rows={3}
                                            className="border-jungle/20 rounded-xl"
                                            data-testid="delivery-address-input"
                                        />
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Notes */}
                            <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 font-heading text-lg text-jungle">
                                        <FileText className="h-5 w-5 text-leaf" />
                                        Notes de commande
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        <Label htmlFor="notes" className="text-jungle">Instructions spéciales (optionnel)</Label>
                                        <Textarea
                                            id="notes"
                                            placeholder="Ex: Livraison entre 8h et 10h, sonner au portail..."
                                            value={formData.notes}
                                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                            rows={3}
                                            className="border-jungle/20 rounded-xl"
                                            data-testid="notes-input"
                                        />
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Payment Info */}
                            <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 font-heading text-lg text-jungle">
                                        <Truck className="h-5 w-5 text-leaf" />
                                        Paiement et livraison
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="bg-jungle/5 rounded-xl p-4 space-y-3">
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-leaf/20 flex items-center justify-center flex-shrink-0">
                                                <FileText className="w-4 h-4 text-leaf" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-jungle">Paiement par facturation</p>
                                                <p className="text-sm text-muted-foreground">Vous recevrez une facture après livraison. Délai de paiement : 30 jours.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-leaf/20 flex items-center justify-center flex-shrink-0">
                                                <Truck className="w-4 h-4 text-leaf" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-jungle">Livraison gratuite</p>
                                                <p className="text-sm text-muted-foreground">Sous 24-48h ouvrées selon disponibilité.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-leaf/20 flex items-center justify-center flex-shrink-0">
                                                <Mail className="w-4 h-4 text-leaf" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-jungle">Confirmation par email</p>
                                                <p className="text-sm text-muted-foreground">Un email de confirmation sera envoyé à {user?.email}</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Confirmation Checkbox */}
                            <Card className="border-0 bg-leaf/10 shadow-sm rounded-2xl">
                                <CardContent className="pt-6">
                                    <div className="flex items-start space-x-3">
                                        <Checkbox 
                                            id="confirm" 
                                            checked={confirmed}
                                            onCheckedChange={setConfirmed}
                                            className="mt-1 border-jungle data-[state=checked]:bg-jungle"
                                            data-testid="confirm-checkbox"
                                        />
                                        <div className="space-y-1">
                                            <label 
                                                htmlFor="confirm" 
                                                className="text-sm font-medium text-jungle cursor-pointer leading-relaxed"
                                            >
                                                Je confirme ma commande et j'accepte les conditions de vente
                                            </label>
                                            <p className="text-xs text-muted-foreground">
                                                En validant, vous recevrez un email de confirmation et notre équipe préparera votre commande.
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Submit Button */}
                            <Button 
                                type="submit" 
                                size="lg" 
                                className={`w-full h-14 rounded-xl text-lg font-semibold transition-all ${
                                    confirmed 
                                        ? 'bg-jungle hover:bg-jungle/90 text-sand' 
                                        : 'bg-jungle/30 text-jungle/50 cursor-not-allowed'
                                }`}
                                disabled={loading || !confirmed}
                                data-testid="submit-order-button"
                            >
                                {loading ? (
                                    <>
                                        <span className="animate-spin mr-2">&#8987;</span>
                                        Validation en cours...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-5 h-5 mr-2" />
                                        Valider ma commande
                                    </>
                                )}
                            </Button>
                        </form>
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <Card className="sticky top-24 border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                            <CardHeader>
                                <CardTitle className="font-heading text-lg text-jungle">Récapitulatif</CardTitle>
                                <p className="text-sm text-muted-foreground">{items.length} article{items.length > 1 ? 's' : ''}</p>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4 max-h-[300px] overflow-y-auto">
                                    {items.map((item) => (
                                        <div key={item.product_id} className="flex gap-3" data-testid={`summary-item-${item.product_id}`}>
                                            <img
                                                src={item.image_url}
                                                alt={item.product_name}
                                                className="w-16 h-16 object-cover rounded-lg"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-jungle truncate">{item.product_name}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {item.package_weight > 0
                                                        ? `${item.quantity} colis x ${getPackageDisplay(item.package_weight, item.unit)} x ${formatPrice(item.price)}${getPricePerUnit(item.unit)}`
                                                        : `${item.quantity} ${item.unit} x ${formatPrice(item.price)}`
                                                    }
                                                </p>
                                            </div>
                                            <p className="text-sm font-semibold text-jungle">
                                                {formatPrice(calcItemTotal(item))}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                <Separator className="my-4 bg-jungle/10" />

                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Sous-total</span>
                                        <span className="text-jungle">{formatPrice(total)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Livraison</span>
                                        <span className="text-leaf font-medium">Gratuite</span>
                                    </div>
                                </div>

                                <Separator className="my-4 bg-jungle/10" />

                                <div className="flex justify-between items-center">
                                    <span className="font-heading font-semibold text-jungle">Total HT</span>
                                    <span className="font-heading text-2xl font-bold text-leaf" data-testid="order-total">
                                        {formatPrice(total)}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};
